import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { fromEvent, merge, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';
import { SystemLatencyService } from 'app/modules/components/synchronization/system-latency/system-latency.service';
import { IntegrationErrorsModalComponent } from 'app/modules/components/synchronization/components/integration-errors-modal/integration-errors-modal.component';
import { ResponsiveDialogService } from 'app/shared/services/responsive-dialog.service';

const POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Badge del header con el conteo de errores de integración SAP del día actual
 * (logs de `log_middleware_service` con status = ERROR). Al hacer clic abre el
 * modal con los registros. Solo se muestra (y consulta) cuando el usuario
 * tiene el permiso de Latencia y al menos una compañía del sistema tiene
 * `ERPActive` — sin integración ERP no hay nada que vigilar. El conteo se
 * refresca cada 10 minutos y al volver a la pestaña.
 */
@Component({
  selector: 'integration-errors-badge',
  standalone: true,
  imports: [
    CommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <button
        mat-icon-button
        matTooltip="Errores de integración SAP"
        (click)="openModal()"
      >
        <mat-icon
          [matBadge]="badgeLabel()"
          [matBadgeHidden]="count() === 0"
          matBadgeColor="warn"
          matBadgeSize="small"
          svgIcon="heroicons_outline:exclamation-triangle"
        ></mat-icon>
      </button>
    }
  `,
})
export class IntegrationErrorsBadgeComponent implements OnInit {
  private readonly companyService = inject(CompanyService);
  private readonly latencyService = inject(SystemLatencyService);
  private readonly responsiveDialog = inject(ResponsiveDialogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = signal(false);
  readonly count = signal(0);
  readonly badgeLabel = computed(() =>
    this.count() > 99 ? '99+' : String(this.count())
  );

  ngOnInit(): void {
    // Nota: en la línea estándar el badge además valida el permiso de Latencia
    // (PermissionService). Esta rama no tiene el sistema de permisos, por lo
    // que el único gate es la compañía ERP-activa.
    // timer(0) difiere el primer request fuera del ciclo inicial de change
    // detection del layout (evita NG0100 en el FuseLoadingBar).
    timer(0)
      .pipe(
        switchMap(() => this.companyService.findToComboBox(true)),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          const anyErpActive = (res?.data ?? []).some((c) => c.ERPActive);
          if (!anyErpActive) return;
          this.visible.set(true);
          this.startPolling();
        },
        error: () => {
          // Sin catálogo de compañías consultable el badge queda oculto y no
          // genera más requests ni errores no controlados.
        },
      });
  }

  openModal(): void {
    this.responsiveDialog
      .open(IntegrationErrorsModalComponent, { size: 'xl' })
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.refreshCount());
  }

  private startPolling(): void {
    const visibilityRefresh$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );

    merge(timer(0, POLL_INTERVAL_MS), visibilityRefresh$)
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        switchMap(() => this.fetchCount()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((total) => this.count.set(total));
  }

  private refreshCount(): void {
    this.fetchCount()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((total) => this.count.set(total));
  }

  // silent: el conteo corre de fondo y no debe encender el overlay global.
  private fetchCount() {
    return this.latencyService.findRecentErrors({ limitPerPage: 1 }, {}, true).pipe(
      map((res) => res.data?.[0]?.pageInfo?.[0]?.total ?? 0),
      catchError(() => of(this.count()))
    );
  }
}
